import React, { useEffect, useMemo, useState } from "react";
import { useSupplierProcessingStore } from "../../../store/useSupplierProcessingStore.ts";
import { useSupplierWarehouses } from "../../../features/supplier-fulfillment/hooks/useSupplierWarehouses";
import { FiSearch } from "react-icons/fi";
import debounce from "lodash/debounce";

const ShipmentFilters = () => {
  const warehouse = useSupplierProcessingStore((s) => s.warehouse);
  const setWarehouse = useSupplierProcessingStore((s) => s.setWarehouse);

  const search = useSupplierProcessingStore((s) => s.search);
  const setSearch = useSupplierProcessingStore((s) => s.setSearch);

  const status = useSupplierProcessingStore((s) => s.status);
  const setStatus = useSupplierProcessingStore((s) => s.setStatus);

  const ordering = useSupplierProcessingStore((s) => s.ordering);
  const setOrdering = useSupplierProcessingStore((s) => s.setOrdering);

  const isPinned = useSupplierProcessingStore((s) => s.isPinned);
  const setIsPinned = useSupplierProcessingStore((s) => s.setIsPinned);


  const { data: warehouses, isLoading, isError } = useSupplierWarehouses();

  const [searchInput, setSearchInput] = useState(search || "");

  const statusLabels = [
    { label: "در انتظار تأیید", value: "PENDING_APPROVING" },
    { label: "در انتظار آماده‌سازی", value: "PENDING_PROCESSING" },
    { label: "در انتظار حمل", value: "READY_FOR_HANDOFF" },
    { label: "در مسیر تحویل", value: "IN_TRANSIT" },
  ];

  const orderingLabels = [
    { label: "جدیدترین", value: "-created_at" },
    { label: "قدیمی‌ترین", value: "created_at" },
    { label: "گران‌ترین", value: "-total_amount" },
    { label: "ارزان‌ترین", value: "total_amount" },
  ];

  const debouncedSetSearch = useMemo(() => {
    return debounce((value) => {
      const normalizedValue = value.trim();

      if (normalizedValue !== search) {
        setSearch(normalizedValue);
      }
    }, 600);
  }, [search, setSearch]);

  useEffect(() => {
    debouncedSetSearch(searchInput);

    return () => {
      debouncedSetSearch.cancel();
    };
  }, [searchInput, debouncedSetSearch]);

  useEffect(() => {
    if (search !== searchInput) {
      setSearchInput(search || "");
    }
    // عمداً searchInput داخل dependency نیست تا با تایپ کاربر loop نسازد.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  useEffect(() => {
    if (warehouses && warehouses.length > 0 && !warehouse) {
      const defaultWarehouse =
        warehouses.find((w) => w.is_default) || warehouses[0];

      if (defaultWarehouse) {
        setWarehouse(String(defaultWarehouse.id));
      }
    }
  }, [warehouses, warehouse, setWarehouse]);

  const handleWarehouseChange = (e) => {
    const value = e.target.value;

    if (value !== warehouse) {
      setWarehouse(value);
    }
  };

  const handleStatusChange = (e) => {
    const value = e.target.value;

    if (value !== status) {
      setStatus(value);
    }
  };

  const handleOrderingChange = (e) => {
    const value = e.target.value;

    if (value !== ordering) {
      setOrdering(value);
    }
  };

  const handlePinnedChange = (e) => {
    const value = e.target.value;

    if (value !== isPinned) {
      setIsPinned(value);
    }
  };


  if (isLoading) {
    return (
      <div className="p-4 text-sm text-gray-500">
        در حال بارگذاری انبارها...
      </div>
    );
  }

  if (isError) {
    return (
      <div className="p-4 text-sm text-red-500">
        خطا در دریافت لیست انبار
      </div>
    );
  }

  return (
    <div className="mb-6 w-full space-y-4" dir="rtl">
      <div className="grid grid-cols-1 gap-4 text-sm md:grid-cols-4">
        <div className="flex flex-col gap-2">
          <label className="mr-1 font-bold text-gray-800">انبار</label>

          <select
              value={warehouse || ""}
              onChange={handleWarehouseChange}
              className="
              w-full rounded-lg border border-gray-200 bg-white px-4 py-2.5
              shadow-sm outline-none transition
              focus:ring-1 focus:ring-blue-500
            "
          >
            <option value="" disabled>
              انتخاب انبار...
            </option>

            {warehouses?.map((w) => (
                <option key={w.id} value={String(w.id)}>
                  {w.title}
                </option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-2">
          <label className="mr-1 font-bold text-gray-800">وضعیت</label>

          <select
              value={status || ""}
              onChange={handleStatusChange}
              className="
              w-full rounded-lg border border-gray-200 bg-white px-4 py-2.5
              shadow-sm outline-none transition
              focus:ring-1 focus:ring-blue-500
            "
          >
            <option value="">همه محموله‌ها</option>

            {statusLabels.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-2">
          <label className="mr-1 font-bold text-gray-800">ترتیب</label>

          <select
              value={ordering || "-created_at"}
              onChange={handleOrderingChange}
              className="
              w-full rounded-lg border border-gray-200 bg-white px-4 py-2.5
              shadow-sm outline-none transition
              focus:ring-1 focus:ring-blue-500
            "
          >
            {orderingLabels.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
            ))}
          </select>
        </div>
        <div className="flex flex-col gap-2">
          <label className="mr-1 font-bold text-gray-800">سنجاق</label>

          <select
              value={isPinned || ""}
              onChange={handlePinnedChange}
              className="
      w-full rounded-lg border border-gray-200 bg-white px-4 py-2.5
      shadow-sm outline-none transition
      focus:ring-1 focus:ring-blue-500
    "
          >
            <option value="">همه محموله‌ها</option>
            <option value="true">فقط سنجاق‌شده‌ها</option>
            <option value="false">بدون سنجاق</option>
          </select>
        </div>

      </div>

      <div className="relative w-full">
        <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-4">
          <FiSearch className="text-xl text-gray-400"/>
        </span>

        <input
            type="text"
            placeholder="جستجو شماره سفارش و نام کالا"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="
            w-full rounded-lg border border-gray-200 py-3 pr-12 pl-4
            text-sm shadow-sm outline-none transition
            placeholder:text-gray-400
            focus:ring-1 focus:ring-blue-500
          "
        />
      </div>
    </div>
  );
};

export default ShipmentFilters;
